import { css } from '@emotion/css';
import { ReactNode } from 'react';
import { FormProvider, UseFormReturn, useWatch } from 'react-hook-form';

import { t, Trans } from '@grafana/i18n';
import { Stack, Button, Field, Input, TextArea, FieldSet, useStyles2 } from '@grafana/ui';

import { ScopeSection } from '../form/AlertEnrichmentForm';
import { EnricherConfigSection } from '../form/EnricherConfigSection';
import type { AlertEnrichmentFormData } from '../form/form';

export type EnrichmentFormMode = 'create' | 'edit';

export interface EnrichmentCreateFormPropsBase {
  form: UseFormReturn<AlertEnrichmentFormData>;
  llmEnabled: boolean;
  isSubmitting: boolean;
  onSubmit: (data: AlertEnrichmentFormData) => Promise<void>;
  onCancel: () => void;
  showScopeSection?: boolean;
}

export interface EnrichmentCreateFormPropsCreate extends EnrichmentCreateFormPropsBase {
  mode: 'create';
}

export interface EnrichmentCreateFormPropsEdit extends EnrichmentCreateFormPropsBase {
  mode: 'edit';
  isReadOnly: boolean;
  provisioningAlert?: ReactNode;
}

export type EnrichmentCreateFormProps = EnrichmentCreateFormPropsCreate | EnrichmentCreateFormPropsEdit;

/**
 * Shared form for create-enrichment (step 2) and edit-enrichment drawers:
 * name, description, timeout, and enricher config. Create mode hides the type selector (chosen in step 1).
 */
export function EnrichmentCreateForm(props: EnrichmentCreateFormProps) {
  const { form, llmEnabled, isSubmitting, onSubmit, onCancel, mode, showScopeSection = false } = props;
  const isCreate = mode === 'create';
  const isReadOnly = mode === 'edit' && props.isReadOnly;
  const provisioningAlert = mode === 'edit' ? props.provisioningAlert : null;
  const hideTypeSelector = isCreate;

  const description = useWatch({ control: form.control, name: 'description', defaultValue: '' });
  const showDescriptionField = !isReadOnly || (typeof description === 'string' && description.trim() !== '');

  const styles = useStyles2(getReadOnlyFormStyles);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack direction="column" gap={4}>
          {provisioningAlert}

          <FieldSet disabled={isReadOnly} className={isReadOnly ? styles.readOnlyFields : undefined}>
            <Stack direction="column" gap={4}>
              <Stack direction="column" gap={3}>
                <Field label={t('alert-enrichment-form.basic-info.name', 'Enrichment Name')} noMargin htmlFor="title">
                  <Input
                    {...form.register('title', {
                      required: t('alert-enrichment-form.basic-info.name-required', 'Name is required'),
                    })}
                    placeholder={t('alert-enrichment-form.basic-info.name-placeholder', 'my-enrichment')}
                    invalid={!!form.formState.errors.title}
                    id="title"
                  />
                </Field>

                {showDescriptionField && (
                  <Field
                    label={t('alert-enrichment-form.basic-info.description', 'Description (Optional)')}
                    noMargin
                    htmlFor="description"
                  >
                    <TextArea {...form.register('description')} rows={2} id="description" />
                  </Field>
                )}

                <Field
                  label={t('alert-enrichment-form.basic-info.timeout', 'Timeout')}
                  noMargin
                  description={t(
                    'alert-enrichment-form.basic-info.timeout-description',
                    'Maximum time allowed for this enrichment (e.g., 30s, 1m)'
                  )}
                  htmlFor="steps.0.timeout"
                >
                  <Input
                    {...form.register('steps.0.timeout')}
                    placeholder={t('alert-enrichment-form.basic-info.timeout-placeholder', '30s')}
                    id="steps.0.timeout"
                  />
                </Field>
              </Stack>

              <EnricherConfigSection
                llmEnabled={llmEnabled}
                hideTypeSelector={hideTypeSelector}
                isReadOnly={isReadOnly}
                typeSelectorDisabled={!isCreate}
              />
              {showScopeSection && <ScopeSection />}
            </Stack>
          </FieldSet>

          <Stack direction="row" gap={1} justifyContent="flex-end" alignItems="center" wrap="wrap">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              {isReadOnly ? (
                <Trans i18nKey="alerting.enrichment.drawer.close">Close</Trans>
              ) : (
                <Trans i18nKey="alerting.common.cancel">Cancel</Trans>
              )}
            </Button>
            {!isReadOnly && (
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  isCreate ? (
                    <Trans i18nKey="alerting.enrichment.create-drawer.creating">Creating...</Trans>
                  ) : (
                    <Trans i18nKey="alerting.enrichment.drawer.saving">Saving...</Trans>
                  )
                ) : isCreate ? (
                  <Trans i18nKey="alerting.enrichment.create-drawer.create">Create</Trans>
                ) : (
                  <Trans i18nKey="alerting.enrichment.drawer.save">Save</Trans>
                )}
              </Button>
            )}
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
}

/** Removes hover/focus border and other interactive styles in read-only view */
function getReadOnlyFormStyles(theme: {
  colors: { action: { disabledBackground: string }; border: { weak: string } };
}) {
  const disabledBorder = theme.colors.action.disabledBackground ?? theme.colors.border.weak;
  return {
    readOnlyFields: css({
      '& input:hover, & input:focus, & textarea:hover, & textarea:focus': {
        borderColor: `${disabledBorder} !important`,
        boxShadow: 'none',
        outline: 'none',
      },
      '& [class*="input"]:hover, & [class*="input"]:focus': {
        borderColor: `${disabledBorder} !important`,
        boxShadow: 'none',
        outline: 'none',
      },
      '& button:hover, & button:focus': {
        outline: 'none',
      },
    }),
  };
}
